import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { Login } from "./pages";
import { useRootStore } from "./stores";
import { AppHeader, AppPopup, NewOrderAlert } from "./components";
import WebViewBridge from "./webViewBridge";
import { BudgetPage, Main, OrdersHistory, PlacePage } from "./pages";
import axios from "axios";
import { CircularProgress } from "@mui/material";

const App = () => {
  const {
    place,
    language,
    setToken,
    fetchPlace,
    token,
    setPlace,
    changeWSConnected,
    notificationPopupOpen,
    notificationPopupMessage,
    setNotificationPopupOpen,
    saveNotificationToken,
  } = useRootStore().rootStore.placeStore;
  const { setCurrentPage } = useRootStore().rootStore.drawerStore;
  const {
    fetchOrders,
    fetchOrdersStatus,
    pendingPushRefetch,
    setPendingPushRefetch,
  } = useRootStore().rootStore.orderStore;
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const navigateRef = useRef<typeof navigate>(navigate);
  const store = useRootStore().rootStore;
  const [trans_loaded, set_trans_loaded] = useState(false);
  const [user_loaded, set_user_loaded] = useState(false);
  const [loading, set_loading] = useState(true);
  const pushInitializedRef = useRef(false);
  const lastResumeFetchRef = useRef(0);

  //const sound = Sound();
  //const [playing, toggle] = useAudio(
  //  'https://storage.googleapis.com/purple2021/media/ar/female/%D8%B7%D9%8E%D9%84%D9%8E%D8%A8%D9%90%D9%8A%D9%8E%20%D8%B1%D9%8E%D9%82%D9%8E%D9%85%D9%92%201%20%D8%A8%D8%A7%D9%84%D8%A7%D9%86%D8%AA%D8%B8%D8%A7%D8%B1%D9%92.mp3'
  //);
  //const aud = new Audio(
  //  'https://storage.googleapis.com/purple2021/media/ar/female/%D8%B7%D9%8E%D9%84%D9%8E%D8%A8%D9%90%D9%8A%D9%8E%20%D8%B1%D9%8E%D9%82%D9%8E%D9%85%D9%92%201%20%D8%A8%D8%A7%D9%84%D8%A7%D9%86%D8%AA%D8%B8%D8%A7%D8%B1%D9%92.mp3'
  //);
  //const audioRef = useRef<HTMLAudioElement | null>(null)
  //const [playing, toggle] = useAudio("./assets/iphone");

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    //console.log(playing);
    //btn_ref.current !== null && btn_ref.current.click()
    //btn_ref.current !== null && btn_ref.current.click()
    //setNotificationPopupOpen(true);

    //toggle()
    //console.log(sound())
    changeLanguage();
    checkUser();

    // Initialize WebView bridge for React Native communication
    const webViewBridge = WebViewBridge.getInstance();
    if (webViewBridge.isReactNative()) {
      console.log("Running in React Native WebView - initializing bridge");
      webViewBridge.initialize(store, ((to: any, options?: any) =>
        navigateRef.current(to, options)) as typeof navigate);
    }

    //toggle();
    //console.log(history);
    //start('https://www.kozco.com/tech/LRMonoPhase4.mp3').then(v => console.log('started')).catch(e => console.error(e));
    return () => {
      webViewBridge.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user_loaded && trans_loaded) set_loading(false);
  }, [user_loaded, trans_loaded]);

  // useEffect(() => {
  //   aud.play()
  // }, [aud])

  const GetTranslations = async () => {
    //const t = await getTranslations();
    //const storage = new Storage();
    //const [metadata] = await storage.bucket('purple2021').getMetadata()
    //console.log(metadata)
    //console.log('again');
    const t = await axios(
      "https://storage.googleapis.com/purple2021/translations/translations.json",
    )
      .then((res) => res.data)
      .catch(() => set_trans_loaded(true));
    if (t !== null && t !== undefined) {
      await i18n.addResourceBundle(
        "ar",
        "translation",
        t.ar.translation,
        true,
        false,
      );
      await i18n.addResourceBundle(
        "heb",
        "translation",
        t.heb.translation,
        true,
        false,
      );
      await i18n.addResourceBundle(
        "en",
        "translation",
        t.en.translation,
        true,
        false,
      );
    }
  };

  const initPushNotifications = () => {
    if (pushInitializedRef.current) return;
    const webViewBridge = WebViewBridge.getInstance();
    if (webViewBridge.isReactNative()) {
      pushInitializedRef.current = true;
      console.log("Initializing push notifications via WebView bridge");
      changeWSConnected(true);
      // Use onToken to handle both proactive tokens (sent by native on WEBVIEW_READY)
      // and requested tokens — whichever arrives first
      webViewBridge.onToken((notificationToken) => {
        saveNotificationToken(notificationToken);
      });
      // Also explicitly request in case native didn't send proactively
      webViewBridge
        .requestPushToken()
        .then((notificationToken) => saveNotificationToken(notificationToken))
        .catch((error) => console.warn("Push token request failed:", error));
    }
  };

  // Trigger push init whenever we have a valid auth token (covers login, app restart, and token refresh).
  // Decoupled from fetchPlace so transient server errors don't block push registration.
  useEffect(() => {
    if (token && !pushInitializedRef.current) {
      initPushNotifications();
    }
    // Reset push state on logout so re-login re-registers
    if (!token) {
      pushInitializedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Pushes received while the wrapper app is backgrounded never reach the
  // WebView (no background task on current native builds), so the order list
  // silently goes stale. Refetch whenever the page becomes visible again, and
  // drain any refetch that a push requested before the auth token was loaded.
  useEffect(() => {
    if (!token) return;

    const refetchOrders = () => {
      const now = Date.now();
      if (now - lastResumeFetchRef.current < 5000) return;
      lastResumeFetchRef.current = now;
      setPendingPushRefetch(false);
      fetchOrders(token).catch((err) =>
        console.error("Order refetch on resume failed:", err),
      );
      fetchOrdersStatus(token).catch((err) =>
        console.error("Order status refetch on resume failed:", err),
      );
    };

    if (pendingPushRefetch) {
      refetchOrders();
    }

    const onBecameVisible = () => {
      if (document.visibilityState === "visible") refetchOrders();
    };
    document.addEventListener("visibilitychange", onBecameVisible);
    window.addEventListener("focus", onBecameVisible);
    return () => {
      document.removeEventListener("visibilitychange", onBecameVisible);
      window.removeEventListener("focus", onBecameVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pendingPushRefetch]);

  const checkUser = async () => {
    //console.log(token);
    if (token === null) {
      let t = await localStorage.getItem("token");
      if (t !== undefined && t !== null) {
        setToken(t);
        await fetchPlace().then(async (data) => {
          if (data === undefined) return;
          if (data.status === 200) {
            setCurrentPage("main");
          } else if (data.status === 401 || data.status === 403) {
            // Only clear credentials on auth errors, not on rate limiting (429)
            localStorage.removeItem("token");
            setPlace(null);
            setToken(null);
          } else {
            // For 429, 500, etc. - keep credentials, just log the error
            console.warn(
              `fetchPlace returned status ${data.status}, keeping session`,
            );
          }
        });
      }
    } else {
      await fetchPlace().then(async (data) => {
        if (data === undefined) return;
        if (data.status === 200) {
          setCurrentPage("main");
        } else if (data.status === 401 || data.status === 403) {
          localStorage.removeItem("token");
          setPlace(null);
          setToken(null);
        } else {
          console.warn(
            `fetchPlace returned status ${data.status}, keeping session`,
          );
        }
      });
    }
    set_user_loaded(true);
  };

  const changeLanguage = async () => {
    //await GetTranslations();
    await GetTranslations();
    //console.log(i18n.getResourceBundle('heb', 'translation'));
    if (language === "arabic") i18n.changeLanguage("ar");
    if (language === "english") i18n.changeLanguage("en");
    if (language === "hebrew") i18n.changeLanguage("heb");
    if (i18n.language !== "en")
      document.getElementsByTagName("html")[0].setAttribute("dir", "rtl");
    else document.getElementsByTagName("html")[0].setAttribute("dir", "ltr");
    WebViewBridge.getInstance().syncLanguage(i18n.language);
    set_trans_loaded(true);
  };

  // const GetTranslations = async () => {
  //   const t = await getTranslations();
  //   console.log(t);
  //   if (t !== null) {
  //     await i18n.addResourceBundle(
  //       'ar',
  //       'translation',
  //       t.ar.translation,
  //       true,
  //       true
  //     );
  //     await i18n.addResourceBundle(
  //       'heb',
  //       'translation',
  //       t.heb.translation,
  //       true,
  //       true
  //     );
  //     await i18n.addResourceBundle(
  //       'en',
  //       'translation',
  //       t.en.translation,
  //       true,
  //       true
  //     );
  //   }
  // };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100vw",
          height: "100vh",
        }}
      >
        <CircularProgress
          className="MuiCircularProgress-colorPrimary"
          size={50}
        />
      </div>
    );

  return (
    <div
      style={{
        fontFamily:
          i18n.language === "ar"
            ? "'Cairo', sans-serif"
            : "'Rubik', sans-serif",
      }}
    >
      <AppHeader />
      <Routes>
        <Route
          path="/"
          element={
            token !== null &&
            place !== undefined &&
            place !== null &&
            place.id !== undefined ? (
              <Navigate to="/main" />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/main"
          element={
            token === null ? (
              <Navigate to="/" />
            ) : place !== undefined && place !== null ? (
              <Main />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/orders-history"
          element={
            token === null ? (
              <Navigate to="/" />
            ) : place !== undefined && place !== null ? (
              <OrdersHistory />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/place"
          element={
            token === null ? (
              <Navigate to="/" />
            ) : place !== undefined && place !== null ? (
              <PlacePage />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/place/budget"
          element={
            token === null ? (
              <Navigate to="/" />
            ) : place !== undefined && place !== null ? (
              <BudgetPage />
            ) : (
              <Navigate to="/" />
            )
          }
        />
      </Routes>
      <NewOrderAlert />
      <AppPopup
        open={notificationPopupOpen}
        setOpen={setNotificationPopupOpen}
        title={t("Notification")}
        description={
          notificationPopupMessage === null
            ? undefined
            : notificationPopupMessage
        }
        xButton
        xButtonOnClick={() => setNotificationPopupOpen(false)}
      />

      {/* <AppPopup
        open={true}
        setOpen={() => null}
        title={t('Notification')}
        description={'dfgdf'}
        button1
        button1OnClick={() => {
          aud.autoplay = true;
        }}
        button1Title='allow'
        button2
        button2OnClick={() => {
          aud.autoplay = true;
        }}
        button2Title='allow2'
        //xButton
        //xButtonOnClick={() => setNotificationPopupOpen(false)}
      /> */}
      {/* <audio
        ref={(ref) => ref !== audioRef && setAudioRef(ref)}
        autoPlay
        src={audioRef?.src}
      /> */}
      {/* <iframe title='fgh' src={require('./assets/sounds/silence.mp3')} allow="autoplay" style={{display: "none"}}>
      </iframe> */}
    </div>
  );
};

export default observer(App);
